import { recordRecentTransferAccount } from '@src/containers/Transfers/transferFormPreferenceUtils';
import { getRecentTransferAccountsKey } from '@src/containers/Transfers/types';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { getAppConfigSliceActions } from '@src/providers/AppConfigRedux/AppConfigSlice';
import { selectFullAppConfig } from '@src/providers/AppConfigRedux/selectors';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { sortByFavoritesAndLabel } from '@src/utils/sorting';
import { useCallback, useMemo, useState } from 'react';

const EMPTY_RECENT_ACCOUNT_NAMES: string[] = [];

export function useTransferPreferences(assetOptions: string[]) {
  const dispatch = useAppStateDispatch();
  const { maxRecentTransferAccounts } = useFeatureFlag();
  const { favoriteTransferAssets, recentTransferSourceAccounts, recentTransferDestinationAccounts } =
    useAppStateSelector(state => selectFullAppConfig(state).transferSettings);
  const { setFavoriteTransferAssets, setRecentTransferSourceAccounts, setRecentTransferDestinationAccounts } =
    getAppConfigSliceActions();
  const favoriteTransferAssetsSet = useMemo(() => new Set(favoriteTransferAssets), [favoriteTransferAssets]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const toggleFavoriteTransferAsset = useCallback(
    (assetToToggle: string) => {
      const isFavorite = favoriteTransferAssets.includes(assetToToggle);
      const nextFavoriteTransferAssets = isFavorite
        ? favoriteTransferAssets.filter(favoriteAsset => favoriteAsset !== assetToToggle)
        : [...favoriteTransferAssets, assetToToggle];

      dispatch(setFavoriteTransferAssets(nextFavoriteTransferAssets));
    },
    [dispatch, favoriteTransferAssets, setFavoriteTransferAssets]
  );

  const sortedAssetOptions = useMemo(
    () =>
      sortByFavoritesAndLabel(
        assetOptions.filter(assetOption => (showOnlyFavorites ? favoriteTransferAssetsSet.has(assetOption) : true)),
        favoriteTransferAssetsSet,
        asset => asset,
        asset => asset
      ),
    [assetOptions, favoriteTransferAssetsSet, showOnlyFavorites]
  );

  const getRecentSourceAccountNames = useCallback(
    (provider: string, asset: string) =>
      recentTransferSourceAccounts[getRecentTransferAccountsKey(provider, asset)] ?? EMPTY_RECENT_ACCOUNT_NAMES,
    [recentTransferSourceAccounts]
  );

  const getRecentDestinationAccountNames = useCallback(
    (provider: string, asset: string) =>
      recentTransferDestinationAccounts[getRecentTransferAccountsKey(provider, asset)] ?? EMPTY_RECENT_ACCOUNT_NAMES,
    [recentTransferDestinationAccounts]
  );

  const recordRecentSource = useCallback(
    (provider: string, asset: string, marketAccountName: string) => {
      if (!provider || !asset) {
        return;
      }

      dispatch(
        setRecentTransferSourceAccounts(
          recordRecentTransferAccount(
            recentTransferSourceAccounts,
            provider,
            asset,
            marketAccountName,
            maxRecentTransferAccounts
          )
        )
      );
    },
    [dispatch, maxRecentTransferAccounts, recentTransferSourceAccounts, setRecentTransferSourceAccounts]
  );

  const recordRecentDestination = useCallback(
    (provider: string, asset: string, marketAccountName: string) => {
      if (!provider || !asset) {
        return;
      }

      dispatch(
        setRecentTransferDestinationAccounts(
          recordRecentTransferAccount(
            recentTransferDestinationAccounts,
            provider,
            asset,
            marketAccountName,
            maxRecentTransferAccounts
          )
        )
      );
    },
    [dispatch, maxRecentTransferAccounts, recentTransferDestinationAccounts, setRecentTransferDestinationAccounts]
  );

  return {
    sortedAssetOptions,
    showOnlyFavorites,
    setShowOnlyFavorites,
    favoriteTransferAssetsSet,
    toggleFavoriteTransferAsset,
    getRecentSourceAccountNames,
    getRecentDestinationAccountNames,
    recordRecentSource,
    recordRecentDestination,
    maxRecentTransferAccounts,
  };
}
